import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Chip,
  Alert
} from '@mui/material';
import loansService, { Loan, UpdateLoanStatusRequest } from '../../services/loansService';

interface LoanStatusDialogProps {
  open: boolean;
  onClose: () => void;
  loan: Loan;
  onUpdate: (statusUpdate: UpdateLoanStatusRequest) => void;
  isLoading: boolean;
}

const LoanStatusDialog: React.FC<LoanStatusDialogProps> = ({
  open,
  onClose,
  loan,
  onUpdate,
  isLoading
}) => {
  const [newStatus, setNewStatus] = useState<Loan['status']>(loan.status);
  const [notes, setNotes] = useState('');
  const [conditions, setConditions] = useState('');

  const nextStatuses = loansService.getNextPossibleStatuses(loan.status);

  const handleSubmit = () => {
    const statusUpdate: UpdateLoanStatusRequest = {
      status: newStatus,
      ...(notes && { notes }),
      ...(conditions && { conditions: conditions.split('\n').filter(c => c.trim()) })
    };

    onUpdate(statusUpdate);
  };

  const handleClose = () => {
    setNewStatus(loan.status);
    setNotes('');
    setConditions('');
    onClose();
  };

  const isStatusChangeValid = newStatus !== loan.status;
  const canUpdate = isStatusChangeValid || notes.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Loan Status</DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current Status
          </Typography>
          <Chip
            label={loan.status}
            color={loansService.getStatusColor(loan.status)}
            sx={{ mt: 0.5 }}
          />
        </Box>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>New Status</InputLabel>
          <Select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value as Loan['status'])}
            label="New Status"
          >
            <MenuItem value={loan.status}>{loan.status} (Current)</MenuItem>
            {nextStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {nextStatuses.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This loan is in a terminal state. No status changes are typically allowed.
          </Alert>
        )}

        <TextField
          fullWidth
          label="Notes"
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this status change..."
          sx={{ mb: 3 }}
        />

        {(newStatus === 'Additional Info Required' || newStatus === 'Approved') && (
          <TextField
            fullWidth
            label="Conditions (one per line)"
            multiline
            rows={3}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            placeholder={newStatus === 'Additional Info Required' 
              ? "Required documents:\n- Financial statements\n- Tax returns\n- Bank statements"
              : "Approval conditions:\n- Maintain insurance\n- Monthly reporting required"
            }
            helperText="Enter each condition on a new line"
          />
        )}

        {isStatusChangeValid && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Status Change Summary:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {loan.status} → {newStatus}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canUpdate || isLoading}
        >
          {isLoading ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanStatusDialog;